import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import type { OnInit } from '@angular/core';
import { Component, Inject } from '@angular/core';
import type { AbstractControl } from '@angular/forms';
import { UntypedFormControl, UntypedFormGroup, Validators } from '@angular/forms';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import type { MatRadioChange } from '@shared/material';
import { FormUtils } from '@shared/utils/form-utils';
import type { GamepadActionType } from '@dronedeploy/rocos-js-sdk';
import type {
  ButtonDef,
  ButtonType,
  CommandV2,
  GamepadButtonAction,
  GamepadControl,
  GamepadUxComponent,
  GamepadUxComponentCommandVersion,
} from '../../models';
import { GamepadButtonTriggerType } from '../../models';
import { FeatureFlagService } from '../../services/feature-flag/feature-flag.service';
import { FeatureFlags } from '../../services/feature-flag/feature-flags';
import { permission } from '../../services/feature-flag/permission-expr';

/** Receivers ship separately from the console, so any other non-blank name is accepted too. */
export const knownDashboardActionNames = ['view.rotate-yaw', 'view.rotate-pitch', 'capture-monitor.capture-pano'];

export class GamepadButtonActionDialogData {
  projectId: string;
  component: GamepadUxComponent;
  model: any;
  controls: GamepadControl[];
  commandsV2: CommandV2[];
  sources: ButtonDef[];
}

@UntilDestroy()
@Component({
  selector: 'app-gamepad-button-action-dialog',
  templateUrl: './gamepad-button-action-dialog.component.html',
  styleUrls: ['./gamepad-button-action-dialog.component.scss'],
})
export class GamepadButtonActionDialogComponent implements OnInit {
  form: UntypedFormGroup = new UntypedFormGroup({});
  model: GamepadButtonAction;
  trigerTypeEnum = GamepadButtonTriggerType;
  controls: GamepadControl[];
  sources: ButtonDef[];
  commandsV2: CommandV2[];

  public get commandId() {
    return this.form?.get('commandId');
  }
  public get commandVersion(): AbstractControl {
    return FormUtils.getControl(this.form, 'commandVersion');
  }
  public get actionName(): AbstractControl {
    return FormUtils.getControl(this.form, 'actionName');
  }

  public get isDashboardAction(): boolean {
    return this.form?.get('actionType')?.value === 'dashboard';
  }

  public get dashboardActionSuggestions(): string[] {
    const typed = ((this.actionName?.value as string) ?? '').trim().toLowerCase();

    return knownDashboardActionNames.filter((name) => name.includes(typed));
  }

  /** An action cannot be modified by the input it fires from — the triggering press would satisfy it. */
  public get modifierOptions(): ButtonDef[] {
    const selectedSource = this.form?.get('source')?.value;

    return (this.sources ?? []).filter((source) => source.type !== selectedSource);
  }

  /** False when the controller offers nothing else to hold. */
  public get canSetModifier(): boolean {
    return this.modifierOptions.length > 0;
  }

  controlsTabEnabled: boolean = false;
  controlAlreadyUsedByThisAction: boolean;
  projectId: string;

  constructor(
    public dialogRef: MatDialogRef<GamepadButtonActionDialogComponent>,
    @Inject(MAT_DIALOG_DATA) private data: GamepadButtonActionDialogData,
    private featureFlagService: FeatureFlagService,
  ) {
    if (data) {
      if (data.model) {
        this.model = data.model;
      }
      if (data.controls) {
        this.controls = data.controls;
      }

      if (data.projectId) {
        this.projectId = data.projectId;
      }

      if (data.commandsV2) {
        this.commandsV2 = data.commandsV2;
      }

      if (data.sources) {
        this.sources = data.sources;
      }
    }

    if (this.model?.commandVersion !== 'commands-v2') {
      this.controlAlreadyUsedByThisAction = true;
    }

    if (this.projectId) {
      // Get ExperimentalFeature.ControlsTab flag status
      this.featureFlagService
        .getPermission(permission(FeatureFlags.Controls).or(FeatureFlags.LegacyAdmin))
        .pipe(untilDestroyed(this))
        .subscribe((x) => {
          this.controlsTabEnabled = x;
        });
    }
  }

  ngOnInit() {
    this.createForm(this.model);
  }

  // Identify if the control option is available
  get isControlsVisible() {
    return this.controlsTabEnabled || this.controlAlreadyUsedByThisAction;
  }

  onSubmit() {
    if (!this.form.valid) {
      this.form.markAsDirty();
      return;
    }

    const data = this.form.value;
    data.source = {
      type: data.source,
    };
    // Button refs, matching `source`, so fromJSON reads both the same way.
    data.modifiers = ((data.modifiers as ButtonType[]) ?? []).map((type) => ({ type }));
    if (this.isDashboardAction) {
      data.actionName = (data.actionName as string).trim();
    }

    this.dialogRef.close(data);
  }

  onCancel() {
    this.dialogRef.close();
  }

  onSwitchcommandVersion(change: MatRadioChange) {
    // Reset command name as command version changed.
    this.commandId.setValue(null);

    const value = change.value;

    this.switchcommandVersion(value);
  }

  setControls(controls: GamepadControl[]) {
    this.controls = controls;
  }

  setSources(sources: ButtonDef[]) {
    this.sources = sources;
  }

  createForm(model?: GamepadButtonAction) {
    // backward compatible, set the defalut command version to controls
    // this default value will be used if commandVersion flag is missing in the gamepad action
    let defaultCommandVersion = 'controls';

    // For new action, set the default to commands-v2
    if (!this.model || this.model.isDashboardAction) {
      defaultCommandVersion = 'commands-v2';
    }

    this.form = new UntypedFormGroup({
      name: new UntypedFormControl(model?.name ? model.name : '', [Validators.required]),
      source: new UntypedFormControl(model?.source ? model.source.type : null, [Validators.required]),
      frequency: new UntypedFormControl(model?.frequency ? model.frequency : 1000, [Validators.required]),
      triggerType: new UntypedFormControl(model?.triggerType ? model.triggerType : GamepadButtonTriggerType.ONCE, [
        Validators.required,
      ]),
      commandId: new UntypedFormControl(model?.commandId ? model.commandId : null, [Validators.required]),
      commandVersion: new UntypedFormControl(model?.commandVersion ? model.commandVersion : defaultCommandVersion, [
        Validators.required,
      ]),
      modifiers: new UntypedFormControl(model?.modifiers?.map((modifier) => modifier.type) ?? []),
      actionType: new UntypedFormControl(model?.actionType ?? 'robot'),
      actionName: new UntypedFormControl(model?.actionName ?? '', [Validators.required, Validators.pattern(/\S/)]),
    });

    this.enableControlsForActionType(this.form.get('actionType').value);
    this.form
      .get('actionType')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((actionType: GamepadActionType) => this.enableControlsForActionType(actionType));

    this.pruneModifiersWhenSourceChanges();
  }

  /** Disabled controls are left out of both validation and the submitted value. */
  private enableControlsForActionType(actionType: GamepadActionType) {
    const robotControls = [this.commandId, this.commandVersion];

    if (actionType === 'dashboard') {
      robotControls.forEach((control) => control.disable({ emitEvent: false }));
      this.actionName.enable({ emitEvent: false });
      return;
    }

    robotControls.forEach((control) => control.enable({ emitEvent: false }));
    this.actionName.disable({ emitEvent: false });
  }

  /**
   * Re-pointing an action at the input it was modified by would otherwise leave a self-reference the
   * picker no longer shows and the operator cannot clear.
   */
  private pruneModifiersWhenSourceChanges() {
    this.form
      .get('source')
      .valueChanges.pipe(untilDestroyed(this))
      .subscribe((selectedSource: ButtonType) => {
        const modifiers = this.form.get('modifiers');
        const retained = ((modifiers.value as ButtonType[]) ?? []).filter((type) => type !== selectedSource);

        if (retained.length !== (modifiers.value as ButtonType[])?.length) {
          modifiers.setValue(retained);
        }
      });
  }

  private switchcommandVersion(commandVersion: GamepadUxComponentCommandVersion) {
    switch (commandVersion) {
      case 'controls':
      case 'commands-v2':
      case 'commands': // this case is for backward compability for old version of command
        this.commandId.setValidators([Validators.required]);
        break;
      default:
        break;
    }
  }
}
